import { useMemo } from "react";
import { cn } from "@/lib/utils";

interface ResponseQualityIndicatorProps {
  text: string;
  subphase: string;
  className?: string;
}

type QualityLevel = "empty" | "vague" | "partial" | "clear";

interface QualityResult {
  level: QualityLevel;
  label: string;
  hint: string;
}

export const ResponseQualityIndicator = ({
  text,
  subphase,
  className,
}: ResponseQualityIndicatorProps) => {
  const quality = useMemo((): QualityResult => {
    const trimmed = text.trim();
    
    if (!trimmed) {
      return {
        level: "empty",
        label: "",
        hint: "",
      };
    }

    const wordCount = trimmed.split(/\s+/).length;
    const hasNumbers = /\d/.test(trimmed);
    const hasTimeframe = /(\d+\s*(tage?|wochen?|monate?|jahr|prozent|%)|bis|ab|ende|anfang|q[1-4]|kw\s*\d)/i.test(trimmed);
    const hasMetric = /(umsatz|conversion|nutzer|kunden|quote|rate|anzahl|budget|kosten|revenue|marge)/i.test(trimmed);
    const hasConcreteAction = /(wir|ich|team|entscheiden|umsetzen|starten|beenden|erreichen|vermeiden)/i.test(trimmed);
    const hasStakeholder = /(team|kunde|investor|partner|geschäftsführ|vorstand|nutzer|user|markt)/i.test(trimmed);
    
    // Calculate quality score
    let score = 0;
    
    // Word count contribution
    if (wordCount >= 10) score += 1;
    if (wordCount >= 25) score += 1;
    if (wordCount >= 50) score += 0.5;
    
    // Specificity indicators
    if (hasNumbers) score += 1;
    if (hasTimeframe) score += 1.5;
    if (hasMetric) score += 1.5;
    if (hasConcreteAction) score += 0.5;
    if (hasStakeholder) score += 0.5;
    
    // Subphase-specific boosts
    if (subphase === "1.1") {
      // Looking for measurable success criteria
      if (hasMetric && hasTimeframe) score += 1;
    } else if (subphase === "1.2") {
      // Looking for tension/conflict description
      if (/vs\.?|versus|oder|einerseits|andererseits|konflikt|spannung/i.test(trimmed)) score += 1;
    } else if (subphase === "1.3") {
      // Looking for hypothesis structure
      if (/wenn|dann|annahme|glaube|vermute|hypothese|davon aus/i.test(trimmed)) score += 1;
    }
    
    // Determine level
    if (score < 2) {
      return {
        level: "vague",
        label: "unscharf",
        hint: subphase === "1.1" 
          ? "Woran erkennst du Erfolg konkret?" 
          : "Kannst du es konkreter beschreiben?",
      };
    } else if (score < 4) {
      return {
        level: "partial",
        label: "teilweise messbar",
        hint: "Ein Zeitrahmen oder eine Metrik würde helfen.",
      };
    } else {
      return {
        level: "clear",
        label: "überprüfbar",
        hint: "",
      };
    }
  }, [text, subphase]);

  if (quality.level === "empty") {
    return null;
  }

  return (
    <div className={cn("flex items-center gap-2 text-xs", className)}>
      <div className="flex items-center gap-1.5">
        <div
          className={cn(
            "w-2 h-2 rounded-full transition-colors duration-300",
            quality.level === "vague" && "bg-destructive/80",
            quality.level === "partial" && "bg-amber-500",
            quality.level === "clear" && "bg-emerald-500"
          )}
        />
        <span
          className={cn(
            "font-medium transition-colors duration-300",
            quality.level === "vague" && "text-destructive/80",
            quality.level === "partial" && "text-amber-600 dark:text-amber-400",
            quality.level === "clear" && "text-emerald-600 dark:text-emerald-400"
          )}
        >
          {quality.label}
        </span>
      </div>
      {quality.hint && (
        <span className="text-muted-foreground/70 hidden sm:inline">
          — {quality.hint}
        </span>
      )}
    </div>
  );
};
